import React, { FC, useMemo } from 'react'
import { LinkAsProvider } from '@moonpig/launchpad-components'
import {
  shouldForwardProp,
  StyleSheetManager,
  ThemeProvider,
} from '@moonpig/launchpad-utils'
import { theme } from '@moonpig/launchpad-theme'
import { AppShellProps, AppShell } from '@moonpig/web-core-app-shell'
import {
  LoggedInProvider,
  LoginModalProvider,
  AuthProvider,
  GoogleOneTapProvider,
  LastLoginProvider,
} from '@moonpig/web-core-auth'
import { FetchProvider, Fetch } from '@moonpig/web-core-fetch'
import {
  PageRequiresLoginProvider,
  RemindersModalProvider,
  NotificationsProvider,
  PlatformProvider,
} from '@moonpig/web-core-context'
import {
  BrowserErrorTracker,
  BrowserErrorTrackerProvider,
} from '@moonpig/web-core-monitoring'
import { LocaleTextProvider as LaunchpadLocaleTextProvider } from '@moonpig/launchpad-localisation'
import { DefaultContentSecurityPolicy } from '@moonpig/web-core-security'
import { useLocale, StoreProvider } from '@moonpig/web-core-stores'
import { createEppoClient, useExperiment } from '@moonpig/web-core-eppo/browser'
import { EppoContext, SessionIdProvider } from '@moonpig/web-core-utils'
import {
  EppoBanditModels,
  EppoBanditVariations,
  EppoConfiguration,
} from '@moonpig/web-core-eppo/types'
import { Region } from '@moonpig/web-core-types'
import { SetupGTM } from '@moonpig/web-core-analytics'
import { Link } from '@moonpig/web-core-link'
import { DefaultSubjectAttributes } from '@moonpig/web-core-eppo/shared/DefaultSubjectAttributes'
import { EppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { CustomerSegment } from '@moonpig/web-core-types-graphql'
import { CreateSailthruCookie } from './CreateSailthruCookie'
import { LoginModal } from './LoginModal'
import { SendGAUserData } from './SendGAUserData'
import { useLoginGuard } from '../../hooks'
import { SendGALoginEvent } from './SendGALoginEvent'
import { CreateIntercomSession } from './CreateIntercomSession/CreateIntercomSession'
import { MixpanelSetup } from './MixPanel/MixpanelSetup'
import { CacheCustomerSegments } from './CacheCustomerSegments/CacheCustomerSegments'
import { CacheEppoCustomerId } from './CacheEppoCustomerId/CacheEppoCustomerId'
import { CreateLastLoginCookie } from './CreateLastLoginCookie'

const DynamicallySetupGTM: FC<{
  pageTracking: { type: string; disableDefaultPageTracking?: boolean }
  pageRequiresLogin: boolean
  enableThirdPartyScripts?: boolean
}> = ({ pageTracking, pageRequiresLogin, enableThirdPartyScripts }) => {
  const validationEnabled =
    useExperiment('analytics-validation', {
      fallback: 'control',
    }) === 'cookie-enabled'

  const stapeEnabled = useExperiment('web-enablement-stape-gtm', {
    fallback: false,
  })

  return (
    <SetupGTM
      enableThirdPartyScripts={enableThirdPartyScripts}
      pageTracking={pageTracking}
      pageRequiresLogin={pageRequiresLogin}
      stapeEnabled={stapeEnabled}
      validationEnabled={validationEnabled}
    />
  )
}

export const AppContainer: FC<
  React.PropsWithChildren<{
    appShellProps: AppShellProps
    authBaseUrl?: string
    browserErrorTracker: BrowserErrorTracker
    enableThirdPartyScripts?: boolean
    fetch: Fetch
    isMobile: boolean
    notifications: React.ReactNode[]
    pageRequiresLogin: boolean
    pageTracking: { type: string; disableDefaultPageTracking?: boolean }
    region: Region
    sessionId: string
    loggedIn: boolean
    eppoConfiguration: EppoConfiguration
    eppoBanditVariations: EppoBanditVariations
    eppoBanditModels: EppoBanditModels
    eppoDefaultSubjectAttributes: DefaultSubjectAttributes
    eppoExperimentOverrides: EppoExperimentOverrides
    customerSegments: Array<CustomerSegment> | null
    eppoCustomerId: string | null
  }>
> = ({
  appShellProps,
  authBaseUrl,
  browserErrorTracker,
  children,
  enableThirdPartyScripts,
  fetch,
  isMobile,
  notifications,
  pageRequiresLogin,
  pageTracking,
  region,
  sessionId,
  loggedIn,
  eppoConfiguration,
  eppoBanditVariations,
  eppoBanditModels,
  eppoDefaultSubjectAttributes,
  eppoExperimentOverrides,
  customerSegments,
  eppoCustomerId,
}) => {
  const eppoClient = createEppoClient({
    flagsConfiguration: eppoConfiguration,
    banditVariations: eppoBanditVariations,
    banditModels: eppoBanditModels,
    isBot: eppoDefaultSubjectAttributes.isBot,
  })

  const eppoContext = useMemo(
    () =>
      eppoClient
        ? {
            client: eppoClient,
            defaultSubjectAttributes: eppoDefaultSubjectAttributes,
            experimentOverrides: eppoExperimentOverrides,
            customerId: eppoCustomerId,
          }
        : null,
    [
      eppoClient,
      eppoDefaultSubjectAttributes,
      eppoExperimentOverrides,
      eppoCustomerId,
    ],
  )

  const { isCustomerLoggedIn } = useLoginGuard(loggedIn, pageRequiresLogin)
  const { language } = useLocale()
  const storeId = region

  return (
    <EppoContext.Provider value={eppoContext}>
      <StoreProvider storeId={storeId}>
        <FetchProvider fetch={fetch}>
          <PageRequiresLoginProvider value={pageRequiresLogin}>
            <LoggedInProvider value={{ loggedIn: isCustomerLoggedIn }}>
              <RemindersModalProvider value={{ show: false }}>
                <NotificationsProvider value={{ notifications }}>
                  <SessionIdProvider sessionId={sessionId}>
                    <MixpanelSetup url={appShellProps.path} />
                    <CacheCustomerSegments
                      segments={customerSegments}
                      isLoggedIn={isCustomerLoggedIn}
                    />
                    <CacheEppoCustomerId
                      customerId={eppoCustomerId}
                      isLoggedIn={isCustomerLoggedIn}
                    />
                    <BrowserErrorTrackerProvider
                      browserErrorTracker={browserErrorTracker}
                    >
                      <LoginModalProvider>
                        <AuthProvider authBaseUrl={authBaseUrl}>
                          <GoogleOneTapProvider>
                            <LastLoginProvider>
                              <PlatformProvider isMobile={isMobile}>
                                <>
                                  <StyleSheetManager
                                    shouldForwardProp={shouldForwardProp}
                                  >
                                    <ThemeProvider theme={theme}>
                                      <LaunchpadLocaleTextProvider
                                        locale={language}
                                      >
                                        <LinkAsProvider
                                          linkAs={
                                            Link as React.ComponentProps<
                                              typeof LinkAsProvider
                                            >['linkAs']
                                          }
                                        >
                                          <AppShell {...appShellProps}>
                                            <DynamicallySetupGTM
                                              enableThirdPartyScripts={
                                                enableThirdPartyScripts
                                              }
                                              pageRequiresLogin={
                                                pageRequiresLogin
                                              }
                                              pageTracking={pageTracking}
                                            />
                                            <SendGALoginEvent />
                                            <SendGAUserData />
                                            <CreateSailthruCookie />
                                            <CreateIntercomSession />
                                            <CreateLastLoginCookie />
                                            <DefaultContentSecurityPolicy />
                                            <LoginModal />
                                            {children}
                                          </AppShell>
                                        </LinkAsProvider>
                                      </LaunchpadLocaleTextProvider>
                                    </ThemeProvider>
                                  </StyleSheetManager>
                                </>
                              </PlatformProvider>
                            </LastLoginProvider>
                          </GoogleOneTapProvider>
                        </AuthProvider>
                      </LoginModalProvider>
                    </BrowserErrorTrackerProvider>
                  </SessionIdProvider>
                </NotificationsProvider>
              </RemindersModalProvider>
            </LoggedInProvider>
          </PageRequiresLoginProvider>
        </FetchProvider>
      </StoreProvider>
    </EppoContext.Provider>
  )
}
